import { ref } from "vue";

const SHOW_DELAY = 200; // ms — don't flash the bar for fast navigations
const HIDE_DELAY = 250; // ms — let the 100% fill render before hiding

const progress = ref(0); // 0..1
const isVisible = ref(false);
let trickleTimer = null;
let showTimer = null;
let hideTimer = null;

export function useRouteLoading() {
  return { progress, isVisible };
}

export function startRouteLoading() {
  // A new navigation may start during the post-finish hide delay — cancel it
  // so the bar doesn't vanish mid-flight
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  // A new navigation started while the previous one is still hiding (within
  // HIDE_DELAY): trickle was stopped and progress pinned to 1 — restart the
  // in-flight fill instead of leaving the bar stuck at full
  if (isVisible.value) {
    if (!trickleTimer) {
      progress.value = 0.08;
      startTrickle();
    }
    return;
  }
  // Already waiting to show (e.g. a redirect chain re-enters the guards) —
  // let the in-flight run continue
  if (showTimer) {
    return;
  }
  // Delay actually showing so fast navigations never flash the bar
  showTimer = setTimeout(() => {
    showTimer = null;
    isVisible.value = true;
    progress.value = 0.08;
    startTrickle();
  }, SHOW_DELAY);
}

export function finishRouteLoading() {
  // Navigation finished before the show-delay elapsed — never show the bar
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
    return;
  }
  stopTrickle();
  // afterEach and onError can both fire for one navigation — stay idempotent
  if (hideTimer) {
    clearTimeout(hideTimer);
  }
  if (!isVisible.value) {
    return;
  }
  progress.value = 1;
  hideTimer = setTimeout(() => {
    hideTimer = null;
    isVisible.value = false;
    progress.value = 0;
  }, HIDE_DELAY);
}

function startTrickle() {
  stopTrickle();
  trickleTimer = setInterval(() => {
    // Ease toward 0.9, slowing as it approaches — never reach 100% until done
    const remaining = 0.9 - progress.value;
    if (remaining > 0.01) {
      progress.value += remaining * 0.1;
    }
  }, 200);
}

function stopTrickle() {
  if (trickleTimer) {
    clearInterval(trickleTimer);
    trickleTimer = null;
  }
}
